import {
  Box,
  type BoxProps,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Heading,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { type ReactNode, useContext } from 'react';
import { useNavigate } from 'react-router';
import { NavLink } from 'react-router-dom';

import { WithTooltip } from '@revelio/core';

import { CustomReportBuilderContext } from '../reports/report-builder/custom-report-builder-provider';
import { reportBuilderStore } from '../reports/report-builder/report-builder.repository';
import { useDataEnrichmentActions } from './data-enrichment/data-enrichment.store';
import styles from './deliverable-step-container.module.css';

interface DeliverableStepContainerProps {
  id: string;
  title: string;
  subtitle?: string;
  titleElement?: ReactNode;
  banner?: ReactNode;
  children: ReactNode;
  previousStepPath?: string;
  submitButtonContent?: ReactNode;
  submitTooltip?: ReactNode;
  isSubmitDisabled?: boolean;
  isSubmitLoading?: boolean;
  isSubmitHidden?: boolean;
  skipable?: boolean;
  onSkip?: () => void;
  onSubmit?: () => void;
  disableFade?: boolean;
  contentContainerProps?: BoxProps;
  showTopDivider?: boolean;
  isDataBuilder?: boolean;
  onReset?: () => void;
  startOverPath: string;
  isReportStep?: boolean;
}

export const DeliverableStepContainer = ({
  id,
  title,
  subtitle,
  titleElement,
  banner,
  children,
  previousStepPath,
  submitButtonContent = 'Continue',
  submitTooltip,
  isSubmitDisabled = false,
  isSubmitLoading = false,
  isSubmitHidden = false,
  skipable = false,
  onSkip,
  onSubmit,
  disableFade,
  contentContainerProps,
  showTopDivider = true,
  isDataBuilder = true,
  onReset = () => null,
  startOverPath,
  isReportStep = false,
}: DeliverableStepContainerProps) => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { setReportTemplate } = useContext(CustomReportBuilderContext);
  const { resetState } = useDataEnrichmentActions();

  const isColumnsAndFiltersSelectStep = id === 'columns_filters_select';

  const onClickStartOver = () => {
    // Report Builder
    if (isReportStep) {
      reportBuilderStore.actions.reset(); // reset middleware reports selections
      setReportTemplate(null); // reset custom reports selections
    }
    // Data Builder
    else {
      resetState();
    }
    navigate(startOverPath);
    onClose();
    onReset();
  };
  const ContinueButton = onSubmit ? (
    <Button
      onClick={onSubmit}
      isDisabled={isSubmitDisabled}
      isLoading={isSubmitLoading}
      hidden={isSubmitHidden}
      borderRadius="4px"
      fontSize="12px"
      fontWeight="600"
      colorScheme="green"
      variant="solid"
      size="sm"
      px="6"
    >
      {submitButtonContent}
    </Button>
  ) : null;

  const StartOverBreadcrumb = isDataBuilder ? (
    <BreadcrumbItem data-testid="start-over">
      <Popover
        placement="top-start"
        returnFocusOnClose={false}
        {...{ isOpen, onOpen, onClose }}
      >
        {({ onClose }) => (
          <>
            <PopoverTrigger>
              <BreadcrumbLink
                fontSize="sm"
                fontWeight="600"
                color="red"
                cursor="pointer"
              >
                Start over
              </BreadcrumbLink>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader color="text.primary" fontWeight="600">
                Confirm Action
              </PopoverHeader>
              <PopoverBody color="text.primary">
                Are you sure you want to start over? This will reset all your
                changes.
              </PopoverBody>
              <PopoverFooter display="flex" justifyContent="flex-end">
                <ButtonGroup size="sm">
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    color="text.primary"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="confirm-start-over"
                    colorScheme="red"
                    onClick={onClickStartOver}
                  >
                    Confirm
                  </Button>
                </ButtonGroup>
              </PopoverFooter>
            </PopoverContent>
          </>
        )}
      </Popover>
    </BreadcrumbItem>
  ) : null;

  const PreviousStepBreadcrumb = previousStepPath ? (
    <BreadcrumbItem data-testid="previous-step">
      <BreadcrumbLink
        as={NavLink}
        to={previousStepPath}
        fontSize="sm"
        fontWeight="600"
        color="text.primary"
      >
        Back
      </BreadcrumbLink>
    </BreadcrumbItem>
  ) : null;

  const SkipButton = skipable ? (
    <Button
      onClick={onSkip}
      borderRadius="4px"
      fontSize="sm"
      fontWeight="600"
      colorScheme="green"
      variant="outline"
      size="sm"
      px="6"
    >
      Skip
    </Button>
  ) : null;

  return (
    <Box
      py={{ base: '4', md: '4' }}
      width="100%"
      data-testid={`deliverables-${id}-box-container`}
    >
      <Stack spacing="5" justify="space-between" height="100%">
        <Box width="100%">
          <Flex
            px={{ base: '4', md: '4' }}
            align="center"
            gap={2}
            width="100%"
            minH={0}
          >
            <Flex align="center" gap={2} flexShrink={0}>
              <Heading
                as="h2"
                fontWeight="600"
                size="md"
                color="text.primary"
                m={0}
                lineHeight="short"
              >
                {title}
              </Heading>
              {subtitle ? (
                <Text
                  color="gray.400"
                  fontWeight="600"
                  fontSize="md"
                  flexShrink={0}
                  m={0}
                  lineHeight="short"
                >
                  {subtitle}
                </Text>
              ) : null}
            </Flex>
            {banner ? (
              <Box flex="1" ml="5px" minW={0}>
                {banner}
              </Box>
            ) : (
              <Box flex="1" minW={0} />
            )}
            {titleElement ? (
              <Flex align="center" flexShrink={0} alignSelf="stretch">
                {titleElement}
              </Flex>
            ) : null}
          </Flex>
          {showTopDivider ? <Divider mt={4} /> : null}
        </Box>

        <Box
          className={
            /* eslint-disable-next-line no-nested-ternary */
            disableFade
              ? ''
              : // biome-ignore lint/style/noNestedTernary: migrated from develop
                isColumnsAndFiltersSelectStep
                ? styles.dataBuilderContainer
                : styles.contentContainer
          }
          px={{ base: '4', md: '4' }}
          flexGrow="1"
          overflow="auto"
          {...contentContainerProps}
        >
          {children}
        </Box>

        <Divider />

        <Flex
          px={{ base: '5', md: '5' }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Breadcrumb color="gray.500" separator="|">
              {StartOverBreadcrumb}
              {PreviousStepBreadcrumb}
            </Breadcrumb>
          </Box>
          <Box>
            {id === 'companies' && skipable ? (
              <Flex gap={2}>
                {SkipButton}
                {ContinueButton}
              </Flex>
            ) : (
              <WithTooltip
                label={submitTooltip}
                showCondition={!!submitTooltip}
              >
                {ContinueButton}
              </WithTooltip>
            )}
          </Box>
        </Flex>
      </Stack>
    </Box>
  );
};
